/**
 * GreenSide News — Revenue Demo sample data.
 *
 * EVERYTHING in this file is illustrative sample data for a pre-contract sales
 * demonstration. No real payments, registrations, sponsors or campaigns exist.
 * All dashboard figures are DERIVED from the arrays below so the numbers
 * reconcile everywhere they appear.
 */

export type Region = {
  id: string;
  city: string;
  state: 'California' | 'Arizona';
  short: string;
};

export const REGIONS: Region[] = [
  { id: 'los-angeles', city: 'Los Angeles', state: 'California', short: 'LA' },
  { id: 'orange-county', city: 'Orange County', state: 'California', short: 'OC' },
  { id: 'inland-empire', city: 'Inland Empire', state: 'California', short: 'IE' },
  { id: 'san-diego', city: 'San Diego', state: 'California', short: 'SD' },
  { id: 'phoenix', city: 'Phoenix', state: 'Arizona', short: 'PHX' },
  { id: 'tucson', city: 'Tucson', state: 'Arizona', short: 'TUC' },
];

export const STATE_GROUPS: { state: Region['state']; regions: Region[] }[] = [
  { state: 'California', regions: REGIONS.filter((r) => r.state === 'California') },
  { state: 'Arizona', regions: REGIONS.filter((r) => r.state === 'Arizona') },
];

export const PIPELINE_STAGES = [
  'Inquiry',
  'Package Selected',
  'Creative Prepared',
  'Editorial Approval',
  'Payment Confirmed',
  'Campaign Scheduled',
  'Live',
  'Performance Report',
  'Renewal Reminder',
];

/* ------------------------------------------------------------------ */
/* Sponsorship packages (illustrative pricing)                        */
/* ------------------------------------------------------------------ */

export type SponsorPackage = {
  id: string;
  name: string;
  price: number | null; // null => custom quote
  cadence: string;
  tagline: string;
  perks: string[];
  featured?: boolean;
};

export const SPONSOR_PACKAGES: SponsorPackage[] = [
  {
    id: 'local',
    name: 'Local Sponsor',
    price: 149,
    cadence: 'per month',
    tagline: 'Single-region visibility',
    perks: ['Homepage sponsor strip', 'One region of your choice', 'Monthly performance summary'],
  },
  {
    id: 'featured',
    name: 'Featured Sponsor',
    price: 299,
    cadence: 'per month',
    tagline: 'Elevated homepage placement',
    perks: ['Featured sponsor banner', 'Up to two regions', 'Newsletter mention', 'Monthly performance summary'],
    featured: true,
  },
  {
    id: 'regional',
    name: 'Regional Partner',
    price: 599,
    cadence: 'per month',
    tagline: 'Multi-region presence',
    perks: ['Presenting banner placement', 'Up to four regions', 'Category exclusivity', 'Quarterly spotlight story'],
  },
  {
    id: 'presenting',
    name: 'Presenting Sponsor',
    price: null,
    cadence: 'custom quote',
    tagline: 'Full-platform partnership',
    perks: ['Site-wide presenting placement', 'All regions', 'Event + newsletter co-branding', 'Dedicated account review'],
  },
];

export function packageById(id: string): SponsorPackage | undefined {
  return SPONSOR_PACKAGES.find((p) => p.id === id);
}

/* ------------------------------------------------------------------ */
/* Tournament promotions (illustrative pricing)                       */
/* ------------------------------------------------------------------ */

export type Promotion = {
  id: string;
  name: string;
  price: number | null; // null => fee-based (3% managed registration)
  duration: string;
  tagline: string;
  perks: string[];
  featured?: boolean;
};

export const TOURNAMENT_PROMOTIONS: Promotion[] = [
  {
    id: 'free',
    name: 'Free Tournament Listing',
    price: 0,
    duration: 'always free',
    tagline: 'Standard calendar listing',
    perks: ['Listed in the tournament calendar', 'Included in regional filters', 'Standard card placement'],
  },
  {
    id: 'featured',
    name: 'Featured Listing',
    price: 49,
    duration: '14 days',
    tagline: 'Stand out in the calendar',
    perks: ['Highlighted card styling', 'Pinned near the top of its month', 'Featured badge'],
    featured: true,
  },
  {
    id: 'spotlight',
    name: 'Regional Spotlight',
    price: 99,
    duration: '30 days',
    tagline: 'Cross-region promotion',
    perks: ['Spotlight block on the region page', 'Up to three regions', 'Newsletter event mention'],
  },
  {
    id: 'premium',
    name: 'Premium Tournament Promotion',
    price: 199,
    duration: '30 days',
    tagline: 'Full editorial package',
    perks: ['Homepage featured placement', 'Dedicated event spotlight', 'Social + newsletter inclusion'],
  },
  {
    id: 'managed',
    name: 'Managed Registration Service',
    price: null,
    duration: '3% platform fee',
    tagline: 'We handle sign-ups for you',
    perks: ['Online registration page', 'Player list management', 'Payout after the event', 'Illustrative 3% platform fee'],
  },
];

export function promotionById(id: string): Promotion | undefined {
  return TOURNAMENT_PROMOTIONS.find((p) => p.id === id);
}

/* ------------------------------------------------------------------ */
/* Sample ledger — active sponsorship campaigns                       */
/* ------------------------------------------------------------------ */

export type CampaignStatus = 'Live' | 'Scheduled' | 'In review' | 'Renewal due';

export type SponsorOrder = {
  id: string;
  sponsor: string;
  packageId: string;
  regionId: string;
  monthly: number;
  status: CampaignStatus;
  impressions: number;
  clicks: number;
  leads: number;
  renewsInDays: number;
};

export const SPONSOR_ORDERS: SponsorOrder[] = [
  { id: 'sp-01', sponsor: 'Pacific Coast Auto Group', packageId: 'presenting', regionId: 'los-angeles', monthly: 1200, status: 'Live', impressions: 48200, clicks: 1510, leads: 62, renewsInDays: 24 },
  { id: 'sp-02', sponsor: 'Sunset Motors', packageId: 'featured', regionId: 'los-angeles', monthly: 299, status: 'Live', impressions: 21400, clicks: 640, leads: 21, renewsInDays: 12 },
  { id: 'sp-03', sponsor: 'Pacific Links Realty', packageId: 'regional', regionId: 'los-angeles', monthly: 599, status: 'Live', impressions: 18900, clicks: 520, leads: 34, renewsInDays: 41 },
  { id: 'sp-04', sponsor: 'Irvine Golf Academy', packageId: 'local', regionId: 'orange-county', monthly: 149, status: 'Live', impressions: 8600, clicks: 210, leads: 9, renewsInDays: 19 },
  { id: 'sp-05', sponsor: 'OC Mortgage Group', packageId: 'regional', regionId: 'orange-county', monthly: 599, status: 'Renewal due', impressions: 17300, clicks: 470, leads: 28, renewsInDays: 5 },
  { id: 'sp-06', sponsor: 'Corona Sports Bar', packageId: 'local', regionId: 'inland-empire', monthly: 149, status: 'Live', impressions: 6400, clicks: 150, leads: 6, renewsInDays: 27 },
  { id: 'sp-07', sponsor: 'IE Auto Plaza', packageId: 'featured', regionId: 'inland-empire', monthly: 299, status: 'Scheduled', impressions: 0, clicks: 0, leads: 0, renewsInDays: 60 },
  { id: 'sp-08', sponsor: 'Torrey Pines Grill', packageId: 'featured', regionId: 'san-diego', monthly: 299, status: 'Live', impressions: 15800, clicks: 430, leads: 17, renewsInDays: 8 },
  { id: 'sp-09', sponsor: 'SD Coastal Credit Union', packageId: 'regional', regionId: 'san-diego', monthly: 599, status: 'Live', impressions: 16600, clicks: 460, leads: 26, renewsInDays: 33 },
  { id: 'sp-10', sponsor: 'Desert Sky Golf', packageId: 'featured', regionId: 'phoenix', monthly: 299, status: 'Live', impressions: 12900, clicks: 340, leads: 14, renewsInDays: 22 },
  { id: 'sp-11', sponsor: 'Phoenix Trophy Co.', packageId: 'local', regionId: 'phoenix', monthly: 149, status: 'In review', impressions: 0, clicks: 0, leads: 0, renewsInDays: 0 },
  { id: 'sp-12', sponsor: 'Sonora Cactus Club', packageId: 'local', regionId: 'tucson', monthly: 149, status: 'Live', impressions: 6100, clicks: 140, leads: 5, renewsInDays: 15 },
  { id: 'sp-13', sponsor: 'Tucson Fairways Inn', packageId: 'featured', regionId: 'tucson', monthly: 299, status: 'Live', impressions: 9800, clicks: 240, leads: 11, renewsInDays: 29 },
];

/* ------------------------------------------------------------------ */
/* Sample ledger — tournament promotion orders                        */
/* ------------------------------------------------------------------ */

export type TournamentOrder = {
  id: string;
  tournament: string;
  promotionId: string;
  regionId: string;
  amount: number;
  status: CampaignStatus;
};

export const TOURNAMENT_ORDERS: TournamentOrder[] = [
  { id: 'to-01', tournament: 'Spring Charity Scramble', promotionId: 'featured', regionId: 'los-angeles', amount: 49, status: 'Live' },
  { id: 'to-02', tournament: 'Coastal Amateur Open', promotionId: 'spotlight', regionId: 'los-angeles', amount: 99, status: 'Live' },
  { id: 'to-03', tournament: 'Junior Skills Classic', promotionId: 'featured', regionId: 'orange-county', amount: 49, status: 'Scheduled' },
  { id: 'to-04', tournament: 'Valley Veterans Cup', promotionId: 'featured', regionId: 'inland-empire', amount: 49, status: 'Live' },
  { id: 'to-05', tournament: 'Harbor Links Invitational', promotionId: 'spotlight', regionId: 'san-diego', amount: 99, status: 'Live' },
  { id: 'to-06', tournament: 'Desert Match Play', promotionId: 'premium', regionId: 'phoenix', amount: 199, status: 'Live' },
  { id: 'to-07', tournament: 'Sonoran Summer Series', promotionId: 'spotlight', regionId: 'tucson', amount: 99, status: 'Live' },
];

/* ------------------------------------------------------------------ */
/* Sample ledger — registration gross handled by the platform         */
/* ------------------------------------------------------------------ */

export const PLATFORM_FEE_RATE = 0.03;

export type RegionRegistration = { regionId: string; gross: number };

export const REGISTRATION_GROSS: RegionRegistration[] = [
  { regionId: 'los-angeles', gross: 4600 },
  { regionId: 'orange-county', gross: 2300 },
  { regionId: 'inland-empire', gross: 1600 },
  { regionId: 'san-diego', gross: 3300 },
  { regionId: 'phoenix', gross: 2600 },
  { regionId: 'tucson', gross: 1400 },
];

/* ------------------------------------------------------------------ */
/* Derived totals — the single reconciliation source                  */
/* ------------------------------------------------------------------ */

const zeroed = (): Record<string, number> => {
  const out: Record<string, number> = {};
  REGIONS.forEach((r) => { out[r.id] = 0; });
  return out;
};

export const SPONSORSHIP_BY_REGION: Record<string, number> = (() => {
  const out = zeroed();
  SPONSOR_ORDERS.forEach((o) => { out[o.regionId] += o.monthly; });
  return out;
})();

export const PROMOTIONS_BY_REGION: Record<string, number> = (() => {
  const out = zeroed();
  TOURNAMENT_ORDERS.forEach((o) => { out[o.regionId] += o.amount; });
  return out;
})();

export const REGISTRATION_FEE_BY_REGION: Record<string, number> = (() => {
  const out = zeroed();
  REGISTRATION_GROSS.forEach((o) => { out[o.regionId] += Math.round(o.gross * PLATFORM_FEE_RATE); });
  return out;
})();

export const TOTAL_BY_REGION: Record<string, number> = (() => {
  const out = zeroed();
  REGIONS.forEach((r) => {
    out[r.id] = SPONSORSHIP_BY_REGION[r.id] + PROMOTIONS_BY_REGION[r.id] + REGISTRATION_FEE_BY_REGION[r.id];
  });
  return out;
})();

export const SPONSORSHIP_TOTAL = sum(Object.values(SPONSORSHIP_BY_REGION));
export const PROMOTIONS_TOTAL = sum(Object.values(PROMOTIONS_BY_REGION));
export const REGISTRATION_FEE_TOTAL = sum(Object.values(REGISTRATION_FEE_BY_REGION));
export const REVENUE_TOTAL = SPONSORSHIP_TOTAL + PROMOTIONS_TOTAL + REGISTRATION_FEE_TOTAL;
export const REGISTRATION_GROSS_TOTAL = sum(REGISTRATION_GROSS.map((r) => r.gross));

function sum(list: number[]): number {
  return list.reduce((a, b) => a + b, 0);
}

export function formatUSD(n: number): string {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

export function ctr(impressions: number, clicks: number): string {
  if (!impressions) return '—';
  return `${((clicks / impressions) * 100).toFixed(1)}%`;
}

/* ------------------------------------------------------------------ */
/* Sample pipeline funnel — this month's flow                         */
/* ------------------------------------------------------------------ */

export const PIPELINE_FUNNEL: { stage: string; count: number }[] = [
  { stage: 'Inquiry', count: 42 },
  { stage: 'Package Selected', count: 27 },
  { stage: 'Creative Prepared', count: 18 },
  { stage: 'Editorial Approval', count: 12 },
  { stage: 'Payment Confirmed', count: 9 },
  { stage: 'Campaign Scheduled', count: 9 },
  { stage: 'Live', count: 9 },
  { stage: 'Performance Report', count: 6 },
  { stage: 'Renewal Reminder', count: 4 },
];

/* ------------------------------------------------------------------ */
/* Registration concept — individual vs foursome                      */
/* ------------------------------------------------------------------ */

export const REGISTRATION_CONCEPT = {
  individual: { label: 'Individual entry', example: 150 },
  foursome: { label: 'Foursome entry', example: 520, saving: 80 },
};

/* ------------------------------------------------------------------ */
/* Per-region sample content for the multi-region explorer            */
/* ------------------------------------------------------------------ */

export type RegionContent = {
  tournaments: string[];
  news: string[];
  sponsors: string[];
  bulletins: string[];
  newsletter: string;
};

export const REGION_CONTENT: Record<string, RegionContent> = {
  'los-angeles': {
    tournaments: ['Spring Charity Scramble — Griffith Park', 'Coastal Amateur Open — Rancho Park', 'LA Junior Skills Classic'],
    news: ['Metro Amateur Championship returns to Rancho Park', 'City courses add twilight tee times'],
    sponsors: ['Pacific Coast Auto Group', 'Sunset Motors', 'Pacific Links Realty'],
    bulletins: ['Looking for a fourth for Saturday at Rancho Park', 'Lost wedge at Griffith Park — Wilson staff'],
    newsletter: 'Weekly Digest — Los Angeles edition',
  },
  'orange-county': {
    tournaments: ['Junior Skills Classic — Newport Beach', 'OC Business Cup — Tustin Ranch'],
    news: ['Youth golf program expands across Orange County', 'New short-course academy opens in Irvine'],
    sponsors: ['Irvine Golf Academy', 'OC Mortgage Group'],
    bulletins: ['Beginner-friendly scramble forming in Anaheim', 'Club repair recommendation in Costa Mesa?'],
    newsletter: 'Weekly Digest — Orange County edition',
  },
  'inland-empire': {
    tournaments: ['Valley Veterans Cup — Moreno Valley', 'Inland Amateur Series — Riverside'],
    news: ['Veterans tournament raises record total in Riverside', 'New driving range opens near Corona'],
    sponsors: ['Corona Sports Bar', 'IE Auto Plaza'],
    bulletins: ['Best twilight rates around Rancho Cucamonga?', 'Charity scramble needs volunteers — Temecula'],
    newsletter: 'Weekly Digest — Inland Empire edition',
  },
  'san-diego': {
    tournaments: ['Harbor Links Invitational — Coronado', 'North County Open — Carlsbad'],
    news: ['Coastal courses report strong fall bookings', 'Junior clinic series added at Balboa Park'],
    sponsors: ['Torrey Pines Grill', 'SD Coastal Credit Union'],
    bulletins: ['Course conditions at Torrey Pines South this week?', 'Thursday league seeking two more players'],
    newsletter: 'Weekly Digest — San Diego edition',
  },
  phoenix: {
    tournaments: ['Desert Match Play — Scottsdale', 'Valley of the Sun Classic'],
    news: ['Desert golf season kicks off with record sign-ups', 'Scottsdale course unveils renovated back nine'],
    sponsors: ['Desert Sky Golf', 'Phoenix Trophy Co.'],
    bulletins: ['Early-morning tee partners wanted in Mesa', 'Best value rounds in the East Valley?'],
    newsletter: 'Weekly Digest — Phoenix edition',
  },
  tucson: {
    tournaments: ['Sonoran Summer Series — Tucson', 'Catalina Foothills Invitational'],
    news: ['Sonoran courses prepare for peak winter play', 'New junior academy opens in Oro Valley'],
    sponsors: ['Sonora Cactus Club', 'Tucson Fairways Inn'],
    bulletins: ['Reliable course conditions around Oro Valley?', 'Weekend foursome filling up — Tucson'],
    newsletter: 'Weekly Digest — Tucson edition',
  },
};

export const REGION_BY_ID: Record<string, Region> = Object.fromEntries(REGIONS.map((r) => [r.id, r]));